import { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FavoriteButton - Bouton étoile pour ajouter/retirer des favoris
 * 
 * @param {string} wordId - ID du mot
 * @param {string} size - Taille (sm, md, lg)
 * @param {string} className - Classes CSS additionnelles
 */
export default function FavoriteButton({ wordId, size = "md", className = "" }) {
  const { user } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && wordId) {
      checkIsFavorite();
    }
  }, [user, wordId]);

  const checkIsFavorite = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/favorites/check/${wordId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsFavorite(data.is_favorite);
      }
    } catch (error) {
      console.error("Error checking favorite:", error);
    }
  };

  const toggleFavorite = async (e) => {
    e.stopPropagation(); // Empêcher la propagation si dans une carte cliquable

    if (!user) {
      alert("Connectez-vous pour ajouter des favoris");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (isFavorite) {
        // Remove from favorites
        const response = await fetch(`${API}/favorites/${wordId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setIsFavorite(false);
        }
      } else {
        // Add to favorites
        const response = await fetch(`${API}/favorites/${wordId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setIsFavorite(true);
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    } finally {
      setLoading(false);
    }
  };

  // Si pas connecté, ne rien afficher
  if (!user) {
    return null;
  }

  // Tailles
  const sizes = {
    sm: "w-7 h-7 p-1.5",
    md: "w-9 h-9 p-2",
    lg: "w-12 h-12 p-3"
  };

  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5"
  };

  return (
    <button
      onClick={toggleFavorite}
      disabled={loading}
      className={`
        rounded-full 
        transition-all 
        duration-300
        ${isFavorite 
          ? 'bg-yellow-500/20 hover:bg-yellow-500/30' 
          : 'bg-muted hover:bg-muted/80'
        }
        disabled:opacity-50 
        disabled:cursor-not-allowed
        active:scale-95
        ${sizes[size]}
        ${className}
      `}
      aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
      title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
    >
      <Star 
        className={`
          ${iconSizes[size]} 
          transition-all
          ${isFavorite 
            ? 'text-yellow-500 fill-yellow-500' 
            : 'text-muted-foreground'
          }
          ${loading ? 'animate-pulse' : ''}
        `} 
      />
    </button>
  );
}
